import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Alert,
  CircularProgress,
  Chip,
  TextField,
  InputAdornment,
  Autocomplete,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Add,
  Visibility,
  ExpandMore,
  ChevronRight,
  Search,
  UnfoldMore,
  UnfoldLess,
  Assignment,
  GridOn,
  CloudUpload,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { listProjects, ProjectResponse } from '../../services/projectApi';
import { getAirports, Airport } from '../../services/airportApi';
import { getOrganizationStats, OrganizationStat, getExecutionPlan, ExecutionPlanResponse } from '../../services/planningExecutionApi';
import { getPlanningSummary, getPhaseDetails } from '../../services/projectApi';
import { ExecutionPlan, ExecutionPhase, ExecutionTask, ExecutionAction, ExecutionStatus } from '../../types';
import { useAuth } from '../../contexts/AuthContext';
import { useModalOpen } from '../../contexts/ModalOpenContext';
import { hasRole, isFraportUser } from '../../services/authApi';
import TaskViewPanel from './TaskViewPanel';

interface ProjectBuilderDashboardProps {
  onSelectProject: (projectId: string | number) => void;
  onCreateNew: () => void;
  /** Optional: open Create from Excel import wizard */
  onCreateFromExcel?: () => void;
  /** Optional: download blank Excel template */
  onDownloadExcelTemplate?: () => void;
  onViewProject: (projectDefinitionId: number) => void;
}

interface ProjectWithDetails extends ProjectResponse {
  percentComplete?: number;
  tenant?: number;
  fraport?: number;
  client?: number;
  organizationStats?: OrganizationStat[];
  organizationStatsLoading?: boolean;
}

const ProjectBuilderDashboard: React.FC<ProjectBuilderDashboardProps> = ({
  onSelectProject: _onSelectProject,
  onCreateNew,
  onCreateFromExcel,
  onDownloadExcelTemplate,
  onViewProject: _onViewProject,
}) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isModalOpen } = useModalOpen();
  const [projects, setProjects] = useState<ProjectWithDetails[]>([]);
  const [airports, setAirports] = useState<Airport[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [userAirportId, setUserAirportId] = useState<number | undefined>(undefined); // Deprecated - kept for backward compatibility
  const [userAirports, setUserAirports] = useState<Airport[]>([]); // All airports where user is manager
  
  // Check if user is Airport Manager or System Admin
  const isManager = user ? hasRole(user, 'Airport Manager') : false;
  const isAdmin = user ? hasRole(user, 'System Admin') : false;
  const isExecAdmin = user ? hasRole(user, 'Exec Admin') : false;
  // Only Fraport user type can create/manipulate projects (Download XLSX, Upload XLSX, New)
  const canCreateProjects = user && isFraportUser(user);
  // Regular users are tenant/fraport/client users (not admin, not manager)
  const isRegularUser = user && !isAdmin && !isExecAdmin && !isManager;

  // State for expand/collapse
  const [expandedSections, setExpandedSections] = React.useState<{
    all: boolean;
    [key: string]: boolean;
  }>({
    all: true, // Default to expanded
  });

  // State for search
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [selectedProjectId, setSelectedProjectId] = useState<number | null>(null);
  
  // State for filters
  const [startDateFilter, setStartDateFilter] = useState<string>('');
  const [endDateFilter, setEndDateFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('ACTIVE');
  
  // Refs for scrolling to projects
  const projectRefs = React.useRef<{ [key: number]: HTMLTableRowElement | null }>({});

  // Task View Panel state
  const [taskViewPanelOpen, setTaskViewPanelOpen] = React.useState(() => {
    const saved = localStorage.getItem('taskViewPanelOpen');
    return saved ? saved === 'true' : false; // Default to closed for returning users
  });

  const handleTaskViewPanelToggle = () => {
    const newState = !taskViewPanelOpen;
    setTaskViewPanelOpen(newState);
    localStorage.setItem('taskViewPanelOpen', String(newState));
  };

  // Helper function to convert date string to Date object in local time
  const dateStringToLocalDate = (dateStr: string | null | undefined): Date | null => {
    if (!dateStr) return null;
    const dateOnly = dateStr.split('T')[0];
    const [year, month, day] = dateOnly.split('-').map(Number);
    if (isNaN(year) || isNaN(month) || isNaN(day)) return null;
    return new Date(year, month - 1, day, 0, 0, 0, 0);
  };

  // Helper function to calculate percent complete for an action
  const calculateActionPercentComplete = (action: ExecutionAction): number => {
    if (action.status === 'COMPLETE' || action.status === 'LOCKED') {
      return 100;
    } else if (action.status === 'IN_PROGRESS') {
      if (action.actualEndDate) {
        if (action.contractedStartDate && action.contractedEndDate && action.actualStartDate) {
          const totalDuration = dateStringToLocalDate(action.contractedEndDate)!.getTime() - dateStringToLocalDate(action.contractedStartDate)!.getTime();
          const actualDuration = dateStringToLocalDate(action.actualEndDate)!.getTime() - dateStringToLocalDate(action.actualStartDate)!.getTime();
          if (totalDuration > 0) {
            const percent = Math.min(100, Math.max(0, (actualDuration / totalDuration) * 100));
            return Math.round(percent);
          }
        }
        return 100;
      }
      return 0;
    }
    return 0; // NOT_STARTED
  };

  // Helper function to calculate percent complete for a task (weighted by action duration)
  const calculateTaskPercentComplete = (task: ExecutionTask): number => {
    if (task.actions.length === 0) {
      return task.status === 'COMPLETE' || task.status === 'LOCKED' ? 100 : 0;
    }

    let totalWeight = 0;
    let completedWeight = 0;

    task.actions.forEach(action => {
      const weight = (typeof action.duration === 'number' && !isNaN(action.duration) && action.duration > 0) 
        ? action.duration 
        : 1;
      totalWeight += weight;
      const actionPercent = calculateActionPercentComplete(action);
      completedWeight += weight * (actionPercent / 100);
    });

    if (totalWeight === 0) return 0;
    const result = Math.round((completedWeight / totalWeight) * 100);
    return isNaN(result) ? 0 : result;
  };

  // Helper function to calculate percent complete for a phase (weighted by task duration)
  const calculatePhasePercentComplete = (phase: ExecutionPhase): number => {
    if (phase.tasks.length === 0) {
      return phase.status === 'COMPLETE' || phase.status === 'LOCKED' ? 100 : 0;
    }

    let totalWeight = 0;
    let completedWeight = 0;

    phase.tasks.forEach(task => {
      const weight = (typeof task.contractedDuration === 'number' && !isNaN(task.contractedDuration) && task.contractedDuration > 0)
        ? task.contractedDuration
        : 1;
      totalWeight += weight;
      const taskPercent = calculateTaskPercentComplete(task);
      completedWeight += weight * (taskPercent / 100);
    });

    if (totalWeight === 0) return 0;
    const result = Math.round((completedWeight / totalWeight) * 100);
    return isNaN(result) ? 0 : result;
  };

  // Helper function to calculate percent complete for a project (weighted by phase duration)
  const calculateProjectPercentComplete = (executionPlan: ExecutionPlan): number => {
    if (executionPlan.phases.length === 0) {
      return 0;
    }

    let totalWeight = 0;
    let completedWeight = 0;

    executionPlan.phases.forEach(phase => {
      const weight = (typeof phase.contractedDuration === 'number' && !isNaN(phase.contractedDuration) && phase.contractedDuration > 0)
        ? phase.contractedDuration
        : 1;
      totalWeight += weight;
      const phasePercent = calculatePhasePercentComplete(phase);
      completedWeight += weight * (phasePercent / 100);
    });

    if (totalWeight === 0) return 0;
    const result = Math.round((completedWeight / totalWeight) * 100);
    return isNaN(result) ? 0 : result;
  };

  // Helper function to convert API ExecutionPlanResponse to ExecutionPlan (simplified version)
  const convertApiResponseToExecutionPlan = (apiResponse: ExecutionPlanResponse): ExecutionPlan => {
    const parseDateStringLocal = (dateStr: string | null | undefined): string | null => {
      if (!dateStr) return null;
      const dateOnly = dateStr.split('T')[0];
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateOnly)) {
        return dateOnly;
      }
      return null;
    };

    const phases: ExecutionPhase[] = apiResponse.phases.map(phase => {
      const tasks: ExecutionTask[] = phase.tasks.map(task => {
        const actions: ExecutionAction[] = task.actions.map(action => ({
          actionId: action.actionId,
          actionType: action.actionType,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          duration: action.duration || (action as any).durationHours || 0,
          assignedTo: [],
          notes: action.notes || '',
          displayOrder: action.displayOrder,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          contractedStartDate: parseDateStringLocal(action.contractedStartDate || (action as any).plannedStartDate),
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          contractedEndDate: parseDateStringLocal(action.contractedEndDate || (action as any).plannedEndDate),
          actualStartDate: parseDateStringLocal(action.actualStartDate),
          actualEndDate: parseDateStringLocal(action.actualEndDate),
          status: action.status as ExecutionStatus,
          kpiScore: action.kpiScore || 0,
          isLocked: action.status === 'LOCKED' || action.status === 'COMPLETE',
        }));

        return {
          taskId: task.taskId,
          name: task.name,
          description: task.description || '',
          assignedUsers: [],
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          contractedStartDate: parseDateStringLocal(task.contractedStartDate || (task as any).plannedStartDate),
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          contractedEndDate: parseDateStringLocal(task.contractedEndDate || (task as any).plannedEndDate),
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          contractedDuration: task.contractedDurationHours || (task as any).plannedDurationHours || 0,
          actualStartDate: parseDateStringLocal(task.actualStartDate),
          actualEndDate: parseDateStringLocal(task.actualEndDate),
          actualDuration: task.actualDurationHours || 0,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          prerequisites: (task as any).prerequisites || (task as any).prerequisiteTaskIds || [],
          parallelExecution: task.parallelExecution || false,
          actions,
          displayOrder: task.displayOrder,
          status: task.status as ExecutionStatus,
          kpiScore: task.kpiScore || 0,
        };
      });

      return {
        phaseId: phase.phaseId,
        name: phase.name,
        description: phase.description || '',
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        contractedStartDate: parseDateStringLocal(phase.contractedStartDate || (phase as any).plannedStartDate),
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        contractedEndDate: parseDateStringLocal(phase.contractedEndDate || (phase as any).plannedEndDate),
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        contractedDuration: phase.contractedDurationHours || (phase as any).plannedDurationHours || 0,
        actualStartDate: parseDateStringLocal(phase.actualStartDate),
        actualEndDate: parseDateStringLocal(phase.actualEndDate),
        actualDuration: phase.actualDurationHours || 0,
        tasks,
        displayOrder: phase.displayOrder,
        status: phase.status as ExecutionStatus,
        kpiScore: phase.kpiScore || 0,
      };
    });

    return {
      projectId: apiResponse.projectId,
      projectName: apiResponse.projectName || '',
      projectStartDate: apiResponse.projectStartDate || '',
      currentProjectDate: apiResponse.currentProjectDate || undefined,
      phases,
      changeOrders: [],
      auditLog: [],
    };
  };

  const handleTaskViewPanelClose = () => {
    setTaskViewPanelOpen(false);
    localStorage.setItem('taskViewPanelOpen', 'false');
  };

  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  // Expand all sections
  const expandAll = () => {
    const newExpanded: { all: boolean; [key: string]: boolean } = { all: true };
    airports.forEach(airport => {
      newExpanded[`airport-${airport.airportId}`] = true;
    });
    newExpanded['unassigned'] = true;
    setExpandedSections(newExpanded);
  };

  // Collapse all sections (except "all" which controls visibility)
  const collapseAll = () => {
    const newExpanded: { all: boolean; [key: string]: boolean } = { all: true }; // Keep "all" expanded
    airports.forEach(airport => {
      newExpanded[`airport-${airport.airportId}`] = false;
    });
    newExpanded['unassigned'] = false;
    setExpandedSections(newExpanded);
  };

  // Note: We don't filter projects - search is only for navigation/jumping to projects

  // Handle project selection from search
  const handleProjectSelect = (projectId: number | null) => {
    if (!projectId) return;
    
    setSelectedProjectId(projectId);
    const project = projects.find(p => p.projectId === projectId);
    if (!project) return;

    // Expand the relevant sections
    const newExpanded = { ...expandedSections };
    newExpanded.all = true; // Ensure "all" is expanded
    
    if (project.airportId) {
      // Expand the airport section
      newExpanded[`airport-${project.airportId}`] = true;
    } else {
      // Expand unassigned section
      newExpanded['unassigned'] = true;
    }
    setExpandedSections(newExpanded);

    // Scroll to the project after a brief delay to allow expansion
    setTimeout(() => {
      const projectRef = projectRefs.current[projectId];
      if (projectRef) {
        // Scroll to top of viewport so selected project is at the top
        projectRef.scrollIntoView({ behavior: 'smooth', block: 'start' });
        // Highlight the row briefly
        projectRef.style.backgroundColor = 'rgba(25, 118, 210, 0.1)';
        setTimeout(() => {
          if (projectRef) {
            projectRef.style.backgroundColor = '';
          }
        }, 2000);
      }
    }, 100);
  };

  // Load airports and projects on mount
  // Load user's airport associations (for Airport Managers) - Phase 2 Sprint 27: Support multiple airports
  useEffect(() => {
    const loadUserAirports = async () => {
      if (isManager && user?.userId) {
        try {
          const airportsResponse = await getAirports();
          // Find ALL airports where user is a manager (not just the first one)
          const assignedAirports = airportsResponse.airports.filter(
            (airport) => airport.managers?.some((m) => m.userId === user.userId)
          );
          
          if (assignedAirports.length > 0) {
            setUserAirports(assignedAirports);
            // Keep userAirportId for backward compatibility (use first airport)
            setUserAirportId(assignedAirports[0].airportId);
          } else {
            console.warn('ProjectBuilderDashboard: No airports found where user is a manager. UserId:', user.userId);
            setUserAirports([]);
            setUserAirportId(undefined);
          }
        } catch (err) {
          console.error('ProjectBuilderDashboard: Error loading user airports:', err);
          setUserAirports([]);
          setUserAirportId(undefined);
        }
      } else if (isAdmin || isExecAdmin) {
        // System Admin and Exec Admin see all airports
        try {
          const airportsResponse = await getAirports();
          setUserAirports(airportsResponse.airports);
        } catch (err) {
          console.error('ProjectBuilderDashboard: Error loading airports for admin:', err);
          setUserAirports([]);
        }
      } else {
        setUserAirports([]);
        setUserAirportId(undefined);
      }
    };

    if (user) {
      loadUserAirports();
    }
  }, [user, isManager, isAdmin, isExecAdmin]);

  useEffect(() => {
    loadAirports();
    loadProjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refresh data when window regains focus (in case data was updated elsewhere). Sprint B: do not refresh while a modal is open.
  useEffect(() => {
    const handleFocus = () => {
      if (isModalOpen) return;
      loadAirports();
      loadProjects();
    };
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userAirportId, isModalOpen]);

  // Sprint B2: Project Browser auto-refresh every 10 minutes; do not refresh while a modal is open.
  useEffect(() => {
    const intervalMs = 10 * 60 * 1000;
    const id = setInterval(() => {
      if (isModalOpen) return;
      loadProjects();
      loadAirports();
    }, intervalMs);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isModalOpen]);
  
  // Reload airports when userAirports or projects change (for manager: show assigned airports + airports with assigned-action projects)
  useEffect(() => {
    loadAirports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userAirports, isManager, isAdmin, isExecAdmin, projects]);

  const loadAirports = async () => {
    try {
      const airportsResponse = await getAirports();
      // Phase 2 Sprint 27: Support multiple airports per manager
      if (isManager && userAirports.length > 0) {
        // Show manager-assigned airports plus any airport that has projects the user can see (assigned-action projects in other airports)
        const assignedAirportIds = userAirports.map(a => a.airportId);
        const projectAirportIds = projects.length > 0
          ? [...new Set(projects.map(p => p.airportId).filter((id): id is number => id != null && id !== undefined))]
          : [];
        const combinedIds = new Set([...assignedAirportIds, ...projectAirportIds]);
        const filteredAirports = airportsResponse.airports.filter(
          airport => combinedIds.has(airport.airportId)
        );
        setAirports(filteredAirports);
      } else if (isManager && projects.length > 0) {
        // Manager with no airport assignment but has assigned-action projects: show airports that have those projects
        const projectAirportIds = [...new Set(projects.map(p => p.airportId).filter((id): id is number => id != null && id !== undefined))];
        const filteredAirports = airportsResponse.airports.filter(
          airport => projectAirportIds.includes(airport.airportId)
        );
        setAirports(filteredAirports);
      } else if (isAdmin || isExecAdmin) {
        // System Admins and Exec Admins see all airports
        setAirports(airportsResponse.airports);
      } else {
        // Regular users see no airports (or all if needed)
        setAirports(airportsResponse.airports);
      }
    } catch (apiError) {
      console.error('Error loading airports:', apiError);
      // Don't show error to user if airports fail - projects can still be displayed
      setAirports([]);
    }
  };

  const loadOrganizationStatsForProject = async (projectId: number): Promise<OrganizationStat[]> => {
    try {
      const stats = await getOrganizationStats(projectId);
      return stats.organizations && Array.isArray(stats.organizations) ? stats.organizations : [];
    } catch (error) {
      console.error('Failed to load organization stats for project', projectId, error);
      return [];
    }
  };

  // Helper function to check if user has assignments in a project
  const userHasAssignmentsInProject = async (projectId: number, projectStatus: string): Promise<boolean> => {
    if (!user?.email || !isRegularUser) {
      return true; // Admins and managers see all projects
    }

    const userEmail = user.email.toLowerCase();

    try {
      // For EXECUTION projects, check execution plan for task/action assignments
      if (projectStatus === 'EXECUTION' || projectStatus === 'COMPLETE') {
        const executionPlan = await getExecutionPlan(projectId);
        const convertedPlan = convertApiResponseToExecutionPlan(executionPlan);
        
        // Check if user is assigned to any task or action
        for (const phase of convertedPlan.phases) {
          for (const task of phase.tasks) {
            // Check task assignments (assignedUsers is array of email strings)
            const taskAssignedUsers = (task as any).assignedUsers;
            if (taskAssignedUsers && Array.isArray(taskAssignedUsers) && taskAssignedUsers.some((email: string) => email.toLowerCase() === userEmail)) {
              return true;
            }
            
            // Check action assignments (assignedTo is array of email strings)
            for (const action of task.actions) {
              const actionAssignedTo = (action as any).assignedTo;
              if (actionAssignedTo && Array.isArray(actionAssignedTo) && actionAssignedTo.some((email: string) => email.toLowerCase() === userEmail)) {
                return true;
              }
            }
          }
        }
      } else {
        // For PLANNING projects, check planning plan for task/action assignments
        const planningSummary = await getPlanningSummary(projectId);
        
        // Check all phases and their tasks
        for (const phase of planningSummary.phases) {
          // Load phase details to get tasks
          const phaseDetails = await getPhaseDetails(projectId, phase.phaseId);
          
          for (const task of phaseDetails.tasks) {
            // Check task assignments (assignedUsers field)
            const taskAssignedUsers = (task as any).assignedUsers;
            if (taskAssignedUsers && Array.isArray(taskAssignedUsers) && taskAssignedUsers.length > 0) {
              if (taskAssignedUsers.some((email: string) => email.toLowerCase() === userEmail)) {
                return true;
              }
            }
            
            // Check action assignments
            for (const action of task.actions) {
              const actionAssignedTo = (action as any).assignedTo;
              if (actionAssignedTo && Array.isArray(actionAssignedTo) && actionAssignedTo.length > 0) {
                if (actionAssignedTo.some((email: string) => email.toLowerCase() === userEmail)) {
                  return true;
                }
              }
            }
          }
        }
      }
      
      return false; // No assignments found
    } catch (error) {
      console.error(`Error checking assignments for project ${projectId}:`, error);
      // If we can't check, don't show the project to be safe
      return false;
    }
  };

  const loadProjects = async () => {
      setLoading(true);
      setError(null);
    
    try {
      // Load actual projects from API
      const projectsResponse = await listProjects({ page: 1, pageSize: 100 });
      let projectsList: ProjectWithDetails[] = projectsResponse.projects.map(project => {
        const projectWithDetails: ProjectWithDetails = {
          projectId: project.projectId,
          name: project.name,
          projectStartDate: project.projectStartDate,
          status: project.status as ProjectResponse['status'],
          airportId: project.airportId,
          templateId: project.templateId,
          projectDefinitionId: project.projectDefinitionId,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          rootSiteId: (project as any).rootSiteId ?? null,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          rootDriveId: (project as any).rootDriveId ?? null,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          rootWebUrl: (project as any).rootWebUrl ?? null,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          libraryName: (project as any).libraryName ?? null,
          createdAt: project.createdAt,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          lastUpdated: (project as any).lastUpdated ?? project.createdAt,
          createdBy: project.createdBy,
          // Calculate percent complete based on status
          // For EXECUTION status, we should load execution plan to get actual progress
          // For now, default to 0% until execution plan is loaded and calculated
          percentComplete: project.status === 'COMPLETE' ? 100 : 0,
          // Placeholder values for tenant/fraport/client - will be populated from organization stats
          tenant: 0,
          fraport: 0,
          client: 0,
          organizationStats: [],
          organizationStatsLoading: false,
        };
        return projectWithDetails;
      });
      
      // For regular users, filter to only show projects where they have assignments
      if (isRegularUser && user?.email) {
        const assignmentChecks = await Promise.all(
          projectsList.map(async (project) => ({
            project,
            hasAssignments: await userHasAssignmentsInProject(project.projectId, project.status)
          }))
        );
        
        projectsList = assignmentChecks
          .filter(check => check.hasAssignments)
          .map(check => check.project);
      }
      
      setProjects(projectsList);

      // Load execution plans for projects in EXECUTION status to calculate percent complete
      const projectsToLoadExecutionPlans = projectsList.filter(
        p => p.status === 'EXECUTION'
      );

      // Load organization stats for projects in EXECUTION or COMPLETE status
      const projectsToLoadStats = projectsList.filter(
        p => p.status === 'EXECUTION' || p.status === 'COMPLETE'
      );

      // Load execution plans and stats in parallel
      const executionPlanPromises: Promise<{ projectId: number; percentComplete: number }>[] = [];
      const statsPromises: Promise<{ projectId: number; stats: OrganizationStat[] }>[] = [];

      // Load execution plans for EXECUTION projects
      if (projectsToLoadExecutionPlans.length > 0) {
        projectsToLoadExecutionPlans.forEach(project => {
          executionPlanPromises.push(
            (async () => {
              try {
                const executionPlanResponse = await getExecutionPlan(project.projectId);
                const executionPlan = convertApiResponseToExecutionPlan(executionPlanResponse);
                const percentComplete = calculateProjectPercentComplete(executionPlan);
                return { projectId: project.projectId, percentComplete };
              } catch (error) {
                console.error(`Failed to load execution plan for project ${project.projectId}:`, error);
                return { projectId: project.projectId, percentComplete: 0 };
              }
            })()
          );
        });
      }

      // Load stats for all projects
      if (projectsToLoadStats.length > 0) {
        projectsToLoadStats.forEach(project => {
          statsPromises.push(
            (async () => {
              const stats = await loadOrganizationStatsForProject(project.projectId);
              return { projectId: project.projectId, stats };
            })()
          );
        });
      }

      // Wait for all loads to complete
      const [executionPlanResults, statsResults] = await Promise.all([
        Promise.all(executionPlanPromises),
        Promise.all(statsPromises),
      ]);

      console.log('loadProjects: Execution plan results:', executionPlanResults);
      console.log('loadProjects: Stats results:', statsResults);

      // Update projects with execution plan percent complete and organization stats
      setProjects(prevProjects => {
        return prevProjects.map(project => {
          let updatedProject = { ...project };

          // Update percent complete from execution plan
          const executionPlanResult = executionPlanResults.find(r => r.projectId === project.projectId);
          if (executionPlanResult) {
            updatedProject.percentComplete = executionPlanResult.percentComplete;
            console.log(`loadProjects: Updated project ${project.projectId} percent complete:`, executionPlanResult.percentComplete);
          }

          // Update organization stats
          const statsResult = statsResults.find(s => s.projectId === project.projectId);
          if (statsResult) {
            console.log(`loadProjects: Processing stats for project ${project.projectId}:`, {
              statsArray: statsResult.stats,
              statsArrayLength: statsResult.stats.length,
              statsArrayTypes: statsResult.stats.map(s => s.type)
            });
            
            const tenantStat = statsResult.stats.find(s => s.type === 'Tenant');
            const fraportStat = statsResult.stats.find(s => s.type === 'Fraport');
            const clientStat = statsResult.stats.find(s => s.type === 'Client');

            const tenantKpi = tenantStat?.kpiScore ?? 0;
            const fraportKpi = fraportStat?.kpiScore ?? 0;
            const clientKpi = clientStat?.kpiScore ?? 0;

            updatedProject = {
              ...updatedProject,
              tenant: tenantKpi,
              fraport: fraportKpi,
              client: clientKpi,
              organizationStats: statsResult.stats,
              organizationStatsLoading: false,
            };

            console.log(`loadProjects: Updated project ${project.projectId} with stats:`, {
              tenant: updatedProject.tenant,
              fraport: updatedProject.fraport,
              client: updatedProject.client,
              organizationStatsLength: updatedProject.organizationStats?.length || 0
            });
          }

          return updatedProject;
        });
      });
    } catch (apiError) {
      // Handle API errors
      let errorMessage = 'Failed to load projects';
      
      if (apiError && typeof apiError === 'object' && 'code' in apiError) {
        if (apiError.code === 'AUTH_REQUIRED') {
          errorMessage = 'Authentication required. Please log in.';
        } else if ('message' in apiError) {
          errorMessage = (apiError as { message: string }).message;
        }
      } else if (apiError && typeof apiError === 'object' && 'message' in apiError) {
        errorMessage = (apiError as { message: string }).message;
      }
      
      setError(errorMessage);
      setProjects([]);
      console.error('Error loading projects:', apiError);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status?.toUpperCase()) {
      case 'COMPLETE':
      case 'COMPLETED':
        return 'success';
      case 'EXECUTION':
      case 'IN_PROGRESS':
        return 'primary';
      case 'PLANNING':
      case 'PENDING':
        return 'warning';
      case 'CANCELLED':
        return 'error';
      default:
        return 'default';
    }
  };

  const formatValue = (value: number | string | undefined) => {
    if (value === undefined || value === null) return '0';
    if (typeof value === 'string') {
      return value;
    }
    return value >= 0 ? `+${value}` : `${value}`;
  };

  // Calculate project total KPI (sum of Tenant + Fraport + Client)
  const getProjectTotalKpi = (project: ProjectWithDetails): number => {
    let tenant = 0;
    let fraport = 0;
    let client = 0;

    if (project.organizationStats && project.organizationStats.length > 0) {
      const tenantStat = project.organizationStats.find(s => s.type === 'Tenant');
      const fraportStat = project.organizationStats.find(s => s.type === 'Fraport');
      const clientStat = project.organizationStats.find(s => s.type === 'Client');
      tenant = tenantStat?.kpiScore ?? project.tenant ?? 0;
      fraport = fraportStat?.kpiScore ?? project.fraport ?? 0;
      client = clientStat?.kpiScore ?? project.client ?? 0;
    } else {
      tenant = project.tenant ?? 0;
      fraport = project.fraport ?? 0;
      client = project.client ?? 0;
    }

    return tenant + fraport + client;
  };

  const handleViewProject = (project: ProjectWithDetails) => {
    // Navigate to project based on status
    // Normalize status to uppercase for case-insensitive comparison
    const status = project.status?.toUpperCase();
    
    console.log('handleViewProject: Project status check', {
      projectId: project.projectId,
      projectName: project.name,
      status: project.status,
      normalizedStatus: status
    });
    
    if (status === 'PLANNING') {
      navigate(`/planning?projectId=${project.projectId}`);
    } else if (status === 'EXECUTION' || status === 'COMPLETE') {
      navigate(`/execution?projectId=${project.projectId}`);
    } else {
      // Default to planning view for unknown/null/undefined status
      // This handles projects that haven't been properly initialized
      console.log('handleViewProject: Unknown status, defaulting to planning view', {
        projectId: project.projectId,
        status: project.status
      });
      navigate(`/planning?projectId=${project.projectId}`);
    }
  };

  // Filter projects based on date range, status, and airport (for Airport Managers)
  // Phase 2 Sprint 27: Show projects from assigned airports OR airports where user has assigned projects
  const filteredProjects = React.useMemo(() => {
    return projects.filter(project => {
      // Airport filter for Airport Managers - show projects from airports in the current airports list (manager + assigned-action airports)
      if (isManager && airports.length > 0 && project.airportId != null) {
        const visibleAirportIds = airports.map(a => a.airportId);
        if (!visibleAirportIds.includes(project.airportId)) {
          return false;
        }
      }

      // Status filter: ACTIVE = exclude archived, cancelled, completed; ALL = show all; otherwise match single status
      if (statusFilter === 'ACTIVE') {
        if (project.status !== 'PLANNING' && project.status !== 'EXECUTION') {
          return false;
        }
      } else if (statusFilter !== 'ALL' && project.status !== statusFilter) {
        return false;
      }

      // Date range filter (filter by projectStartDate)
      if (startDateFilter || endDateFilter) {
        const projectStartDate = project.projectStartDate;
        if (!projectStartDate) {
          // If project has no start date and we have date filters, exclude it
          return false;
        }

        const projectDate = new Date(projectStartDate);
        projectDate.setHours(0, 0, 0, 0); // Normalize to start of day

        if (startDateFilter) {
          const startDate = new Date(startDateFilter);
          startDate.setHours(0, 0, 0, 0);
          if (projectDate < startDate) {
            return false;
          }
        }

        if (endDateFilter) {
          const endDate = new Date(endDateFilter);
          endDate.setHours(23, 59, 59, 999); // End of day
          if (projectDate > endDate) {
            return false;
          }
        }
      }

      return true;
    });
  }, [projects, startDateFilter, endDateFilter, statusFilter, isManager, airports]);

  // Group filtered projects by airport
  const projectsByAirport = React.useMemo(() => {
    const grouped: Record<number, ProjectWithDetails[]> = {};
    const unassigned: ProjectWithDetails[] = [];
    
    filteredProjects.forEach(project => {
      if (project.airportId) {
        if (!grouped[project.airportId]) {
          grouped[project.airportId] = [];
        }
        grouped[project.airportId].push(project);
      } else {
        unassigned.push(project);
      }
    });
    
    return { grouped, unassigned };
  }, [filteredProjects]);

  // Get filtered projects for autocomplete (for search dropdown)
  const projectOptions = React.useMemo(() => {
    return filteredProjects.map(project => ({
      id: project.projectId,
      label: project.name,
      airport: airports.find(a => a.airportId === project.airportId)?.name || 'Unassigned',
      project: project,
    }));
  }, [filteredProjects, airports]);

  // Calculate totals - sum organization stats from filtered projects
  // Use useMemo to recalculate when filtered projects or their organization stats change
  const { totalTenant, totalFraport, totalClient, totalProjectKpi } = React.useMemo(() => {
    const tenant = filteredProjects.reduce((sum, p) => {
      // Use tenant KPI score from organization stats if available, otherwise use tenant property
      if (p.organizationStats && p.organizationStats.length > 0) {
        const tenantStat = p.organizationStats.find(s => s.type === 'Tenant');
        return sum + (tenantStat?.kpiScore || p.tenant || 0);
      }
      return sum + (p.tenant || 0);
    }, 0);
    const fraport = filteredProjects.reduce((sum, p) => {
      // Use fraport KPI score from organization stats if available, otherwise use fraport property
      if (p.organizationStats && p.organizationStats.length > 0) {
        const fraportStat = p.organizationStats.find(s => s.type === 'Fraport');
        return sum + (fraportStat?.kpiScore || p.fraport || 0);
      }
      return sum + (p.fraport || 0);
    }, 0);
    const client = filteredProjects.reduce((sum, p) => {
      // Use client KPI score from organization stats if available, otherwise use client property
      if (p.organizationStats && p.organizationStats.length > 0) {
        const clientStat = p.organizationStats.find(s => s.type === 'Client');
        return sum + (clientStat?.kpiScore || p.client || 0);
      }
      return sum + (p.client || 0);
    }, 0);
    const projectKpi = tenant + fraport + client;
    return { totalTenant: tenant, totalFraport: fraport, totalClient: client, totalProjectKpi: projectKpi };
  }, [filteredProjects]);
  
  const totalProjects = filteredProjects.length;

  // Helper to calculate airport totals - sum organization stats from projects in this airport
  // Use useMemo to cache calculations
  const getAirportTotals = React.useCallback((airportProjects: ProjectWithDetails[]) => {
    const tenant = airportProjects.reduce((sum, p) => {
      if (p.organizationStats && p.organizationStats.length > 0) {
        const tenantStat = p.organizationStats.find(s => s.type === 'Tenant');
        return sum + (tenantStat?.kpiScore || p.tenant || 0);
      }
      return sum + (p.tenant || 0);
    }, 0);
    const fraport = airportProjects.reduce((sum, p) => {
      if (p.organizationStats && p.organizationStats.length > 0) {
        const fraportStat = p.organizationStats.find(s => s.type === 'Fraport');
        return sum + (fraportStat?.kpiScore || p.fraport || 0);
      }
      return sum + (p.fraport || 0);
    }, 0);
    const client = airportProjects.reduce((sum, p) => {
      if (p.organizationStats && p.organizationStats.length > 0) {
        const clientStat = p.organizationStats.find(s => s.type === 'Client');
        return sum + (clientStat?.kpiScore || p.client || 0);
      }
      return sum + (p.client || 0);
    }, 0);
    
    const projectKpi = tenant + fraport + client;
    return {
      count: airportProjects.length,
      tenant,
      fraport,
      client,
      projectKpi,
    };
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ color: 'white' }}>Project Browser</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="contained"
            startIcon={<Assignment />}
            onClick={handleTaskViewPanelToggle}
            size="large"
            sx={{ mr: 1 }}
          >
            Tasks
          </Button>
          {canCreateProjects && (
            <>
              {onDownloadExcelTemplate && (
                <Button
                  variant="contained"
                  startIcon={<GridOn />}
                  onClick={onDownloadExcelTemplate}
                  size="large"
                >
                  Download XLSX
                </Button>
              )}
              {onCreateFromExcel && (
                <Button
                  variant="contained"
                  startIcon={<CloudUpload />}
                  onClick={onCreateFromExcel}
                  size="large"
                >
                  Upload XLSX
                </Button>
              )}
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={onCreateNew}
                size="large"
              >
                New
              </Button>
            </>
          )}
        </Box>
      </Box>

      {/* Search and Expand/Collapse Controls */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          {/* Search Autocomplete */}
          <Autocomplete
            options={projectOptions}
            getOptionLabel={(option) => `${option.label} (${option.airport})`}
            value={projectOptions.find(opt => opt.id === selectedProjectId) || null}
            onChange={(_event, newValue) => {
              if (newValue) {
                handleProjectSelect(newValue.id);
                setSearchQuery(newValue.label);
              } else {
                setSelectedProjectId(null);
                setSearchQuery('');
              }
            }}
            inputValue={searchQuery}
            onInputChange={(_event, newInputValue) => {
              setSearchQuery(newInputValue);
              if (!newInputValue) {
                setSelectedProjectId(null);
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Search projects by name, ID, or airport..."
                variant="outlined"
                size="small"
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
                sx={{ minWidth: 300, flex: '1 1 auto' }}
              />
            )}
            sx={{ flex: '1 1 300px', minWidth: 300 }}
          />

          {/* Expand/Collapse All Buttons */}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<UnfoldMore />}
              onClick={expandAll}
              size="small"
              aria-label="Expand all"
            >
            </Button>
            <Button
              variant="outlined"
              startIcon={<UnfoldLess />}
              onClick={collapseAll}
              size="small"
              aria-label="Collapse all"
            >
            </Button>
          </Box>

          {/* Second row: Filters */}
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', mt: 2 }}>
            {/* Date Range Filters */}
            <TextField
              label="Start Date From"
              type="date"
              value={startDateFilter}
              onChange={(e) => setStartDateFilter(e.target.value)}
              variant="outlined"
              size="small"
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 180 }}
            />
            <TextField
              label="Start Date To"
              type="date"
              value={endDateFilter}
              onChange={(e) => setEndDateFilter(e.target.value)}
              variant="outlined"
              size="small"
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 180 }}
            />

            {/* Status Filter */}
            <FormControl variant="outlined" size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                label="Status"
              >
                <MenuItem value="ACTIVE">Active only</MenuItem>
                <MenuItem value="ALL">All Statuses</MenuItem>
                <MenuItem value="PLANNING">Planning</MenuItem>
                <MenuItem value="EXECUTION">Execution</MenuItem>
                <MenuItem value="COMPLETE">Complete</MenuItem>
                <MenuItem value="CANCELLED">Cancelled</MenuItem>
                <MenuItem value="ARCHIVED">Archived</MenuItem>
              </Select>
            </FormControl>

            {/* Clear Filters Button */}
            {(startDateFilter || endDateFilter || statusFilter !== 'ACTIVE') && (
              <Button
                variant="outlined"
                onClick={() => {
                  setStartDateFilter('');
                  setEndDateFilter('');
                  setStatusFilter('ACTIVE');
                }}
                size="small"
              >
                Clear Filters
              </Button>
            )}
          </Box>
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Empty state for managers with no airports assigned */}
      {isManager && userAirports.length === 0 && !loading && (
        <Alert severity="info" sx={{ mb: 2 }}>
          No airports assigned. Please contact a System Admin to assign you to an airport.
        </Alert>
      )}

      {/* Empty state for managers with airports but no projects */}
      {isManager && userAirports.length > 0 && filteredProjects.length === 0 && !loading && (
        <Alert severity="info" sx={{ mb: 2 }}>
          No projects found for your assigned airports.
        </Alert>
      )}

      {filteredProjects.length === 0 && (!isManager || userAirports.length === 0) ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {projects.length === 0 ? 'No projects found' : 'No projects match the current filters'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {projects.length === 0 
              ? 'Create your first project to get started.'
              : 'Try adjusting your date range or status filters.'}
          </Typography>
          {canCreateProjects && (
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={onCreateNew}
          >
            New
          </Button>
          )}
        </Paper>
      ) : (
        <Paper sx={{ mb: 3 }}>
          <TableContainer>
          <Table>
            <TableHead>
                <TableRow sx={{ backgroundColor: 'grey.100' }}>
                  <TableCell sx={{ fontWeight: 'bold' }}></TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}></TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}></TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>TENANT</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>FRAPORT</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>CLIENT</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>ACTIONS</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
                {/* Main ALL PROJECTS row */}
                <TableRow sx={{ backgroundColor: 'grey.50' }}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <IconButton
                        size="small"
                        onClick={() => toggleSection('all')}
                        sx={{ mr: 1, p: 0.5 }}
                        aria-label={expandedSections.all ? 'collapse all projects' : 'expand all projects'}
                      >
                        {expandedSections.all ? (
                          <ExpandMore fontSize="small" />
                        ) : (
                          <ChevronRight fontSize="small" />
                        )}
                      </IconButton>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                      {totalProjects}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 'bold',
                        // Positive = overages/late (red), Negative = saved time/ahead (green), Zero = on time (default)
                        color: totalProjectKpi > 0 ? 'error.main' : totalProjectKpi < 0 ? 'success.main' : 'text.primary',
                      }}
                    >
                      {formatValue(totalProjectKpi)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 'bold',
                        // Positive = overages/late (red), Negative = saved time/ahead (green), Zero = on time (default)
                        color: totalTenant > 0 ? 'error.main' : totalTenant < 0 ? 'success.main' : 'text.primary',
                      }}
                    >
                      {formatValue(totalTenant)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 'bold',
                        // Positive = overages/late (red), Negative = saved time/ahead (green), Zero = on time (default)
                        color: totalFraport > 0 ? 'error.main' : totalFraport < 0 ? 'success.main' : 'text.primary',
                      }}
                    >
                      {formatValue(totalFraport)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 'bold',
                        // Positive = overages/late (red), Negative = saved time/ahead (green), Zero = on time (default)
                        color: totalClient > 0 ? 'error.main' : totalClient < 0 ? 'success.main' : 'text.primary',
                      }}
                    >
                      {formatValue(totalClient)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {/* No view button at top level */}
                  </TableCell>
                </TableRow>
                {/* Airport rows - only show if ALL PROJECTS is expanded */}
                {expandedSections.all && airports.map((airport) => {
                  const airportProjects = projectsByAirport.grouped[airport.airportId] || [];
                  const isExpanded = expandedSections[`airport-${airport.airportId}`] ?? false;
                  const airportTotals = getAirportTotals(airportProjects);
                  
                  if (airportProjects.length === 0) {
                    return null; // Don't show airports with no projects
                  }
                  
                  return (
                    <React.Fragment key={airport.airportId}>
                      {/* Airport summary row */}
                      <TableRow sx={{ backgroundColor: 'grey.100' }}>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', pl: 4 }}>
                            <IconButton
                              size="small"
                              onClick={() => toggleSection(`airport-${airport.airportId}`)}
                              sx={{ mr: 1, p: 0.5 }}
                              aria-label={isExpanded ? `collapse ${airport.name}` : `expand ${airport.name}`}
                            >
                              {isExpanded ? (
                                <ExpandMore fontSize="small" />
                              ) : (
                                <ChevronRight fontSize="small" />
                              )}
                            </IconButton>
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                              {airport.name}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                            {airportTotals.count}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 'bold',
                              // Positive = overages/late (red), Negative = saved time/ahead (green), Zero = on time (default)
                              color: airportTotals.projectKpi > 0 ? 'error.main' : airportTotals.projectKpi < 0 ? 'success.main' : 'text.primary',
                            }}
                          >
                            {formatValue(airportTotals.projectKpi)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 'bold',
                              // Positive = overages/late (red), Negative = saved time/ahead (green), Zero = on time (default)
                              color: airportTotals.tenant > 0 ? 'error.main' : airportTotals.tenant < 0 ? 'success.main' : 'text.primary',
                            }}
                          >
                            {formatValue(airportTotals.tenant)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 'bold',
                              // Positive = overages/late (red), Negative = saved time/ahead (green), Zero = on time (default)
                              color: airportTotals.fraport > 0 ? 'error.main' : airportTotals.fraport < 0 ? 'success.main' : 'text.primary',
                            }}
                          >
                            {formatValue(airportTotals.fraport)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 'bold',
                              // Positive = overages/late (red), Negative = saved time/ahead (green), Zero = on time (default)
                              color: airportTotals.client > 0 ? 'error.main' : airportTotals.client < 0 ? 'success.main' : 'text.primary',
                            }}
                          >
                            {formatValue(airportTotals.client)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {/* No view button at airport level */}
                        </TableCell>
                      </TableRow>
                      {/* Project rows for this airport - only show if airport is expanded */}
                      {isExpanded && airportProjects.map((project, projectIndex) => {
                          const isSelected = selectedProjectId === project.projectId;
                          // Alternate background every 2 rows (every project = 2 rows)
                          // Use darker gray for alternate projects to make them more visually distinct
                          const rowGroupIndex = Math.floor(projectIndex);
                          const isEvenGroup = rowGroupIndex % 2 === 0;
                          const firstRowBg = isSelected 
                            ? 'rgba(25, 118, 210, 0.1)' 
                            : isEvenGroup 
                              ? 'grey.25' 
                              : 'grey.200'; // ~15% darker gray for alternate projects
                          const secondRowBg = isSelected 
                            ? 'rgba(25, 118, 210, 0.1)' 
                            : isEvenGroup 
                              ? 'grey.25' 
                              : 'grey.200'; // ~15% darker gray for alternate projects
                          return (
                          <React.Fragment key={project.projectId}>
                            {/* First row: Project name and financials */}
                            <TableRow 
                              ref={(el) => {
                                if (el) {
                                  projectRefs.current[project.projectId] = el;
                                }
                              }}
                              sx={{ 
                                backgroundColor: firstRowBg,
                                borderLeft: isSelected ? '3px solid #1976d2' : 'none',
                              }}
                            >
                            <TableCell>
                              <Box sx={{ pl: 8, display: 'flex', alignItems: 'center' }}>
                                <Typography variant="body2" sx={{ mr: 1, color: 'text.secondary' }}>
                                  ••
                                </Typography>
                                <Typography 
                                  variant="body1"
                                  component="span"
                                  role="button"
                                  tabIndex={0}
                                  sx={{ 
                                    fontWeight: 'bold',
                                    color: 'primary.main',
                                    fontSize: '1.1rem',
                                    cursor: 'pointer',
                                    '&:hover': { textDecoration: 'underline' }
                                  }}
                                  onClick={() => handleViewProject(project)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                      e.preventDefault();
                                      handleViewProject(project);
                                    }
                                  }}
                                >
                                  {project.name}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              {/* Blank for project level */}
                            </TableCell>
                            <TableCell>
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 'bold',
                                  // Positive = overages/late (red), Negative = saved time/ahead (green), Zero = on time (default)
                                  color: (() => {
                                    const totalKpi = getProjectTotalKpi(project);
                                    return totalKpi > 0 ? 'error.main' : totalKpi < 0 ? 'success.main' : 'text.primary';
                                  })(),
                                }}
                              >
                                {formatValue(getProjectTotalKpi(project))}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography
                                variant="body2"
                                sx={{
                                  color: (() => {
                                    if (project.organizationStats && project.organizationStats.length > 0) {
                                      const tenantStat = project.organizationStats.find(s => s.type === 'Tenant');
                                      const value = tenantStat?.kpiScore ?? project.tenant ?? 0;
                                      // Positive = overages/late (red), Negative = saved time/ahead (green), Zero = on time (default)
                                      return value > 0 ? 'error.main' : value < 0 ? 'success.main' : 'text.primary';
                                    }
                                    const value = project.tenant || 0;
                                    return value > 0 ? 'error.main' : value < 0 ? 'success.main' : 'text.primary';
                                  })(),
                                }}
                              >
                                {(() => {
                                  if (project.organizationStats && project.organizationStats.length > 0) {
                                    const tenantStat = project.organizationStats.find(s => s.type === 'Tenant');
                                    return formatValue(tenantStat?.kpiScore ?? project.tenant ?? 0);
                                  }
                                  return formatValue(project.tenant ?? 0);
                                })()}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography
                                variant="body2"
                                sx={{
                                  color: (() => {
                                    if (project.organizationStats && project.organizationStats.length > 0) {
                                      const fraportStat = project.organizationStats.find(s => s.type === 'Fraport');
                                      const value = fraportStat?.kpiScore ?? project.fraport ?? 0;
                                      // Positive = overages/late (red), Negative = saved time/ahead (green), Zero = on time (default)
                                      return value > 0 ? 'error.main' : value < 0 ? 'success.main' : 'text.primary';
                                    }
                                    const value = project.fraport || 0;
                                    return value > 0 ? 'error.main' : value < 0 ? 'success.main' : 'text.primary';
                                  })(),
                                }}
                              >
                                {(() => {
                                  if (project.organizationStats && project.organizationStats.length > 0) {
                                    const fraportStat = project.organizationStats.find(s => s.type === 'Fraport');
                                    return formatValue(fraportStat?.kpiScore ?? project.fraport ?? 0);
                                  }
                                  return formatValue(project.fraport ?? 0);
                                })()}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography
                                variant="body2"
                                sx={{
                                  color: (() => {
                                    if (project.organizationStats && project.organizationStats.length > 0) {
                                      const clientStat = project.organizationStats.find(s => s.type === 'Client');
                                      const value = clientStat?.kpiScore ?? project.client ?? 0;
                                      // Positive = overages/late (red), Negative = saved time/ahead (green), Zero = on time (default)
                                      return value > 0 ? 'error.main' : value < 0 ? 'success.main' : 'text.primary';
                                    }
                                    const value = project.client || 0;
                                    return value > 0 ? 'error.main' : value < 0 ? 'success.main' : 'text.primary';
                                  })(),
                                }}
                              >
                                {(() => {
                                  if (project.organizationStats && project.organizationStats.length > 0) {
                                    const clientStat = project.organizationStats.find(s => s.type === 'Client');
                                    return formatValue(clientStat?.kpiScore ?? project.client ?? 0);
                                  }
                                  return formatValue(project.client ?? 0);
                                })()}
                              </Typography>
                            </TableCell>
                            <TableCell>
                      <IconButton
                        size="small"
                                aria-label={`view ${project.name}`}
                                onClick={() => handleViewProject(project)}
                      >
                        <Visibility fontSize="small" />
                      </IconButton>
                            </TableCell>
                          </TableRow>
                          {/* Second row: Status, dates, % complete */}
                          <TableRow
                            sx={{
                              backgroundColor: secondRowBg,
                            }}
                          >
                            <TableCell>
                              <Box sx={{ pl: 8 }}>
                                <Chip
                                  label={project.status || 'UNKNOWN'}
                                  color={getStatusColor(project.status || '') as 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'}
                                  size="small"
                                />
                              </Box>
                            </TableCell>
                            <TableCell>
                              {/* Blank for project level */}
                            </TableCell>
                            <TableCell>
                              {/* Blank for project level - PROJECT +/- column */}
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                {project.projectStartDate ? `start: ${format(new Date(project.projectStartDate), 'MM/dd/yyyy')}` : ''}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              {/* Blank - N/A text removed */}
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box
                                  sx={{
                                    width: 60,
                                    height: 8,
                                    backgroundColor: 'grey.300',
                                    borderRadius: 1,
                                    overflow: 'hidden',
                                  }}
                                >
                                  <Box
                                    sx={{
                                      width: `${project.percentComplete || 0}%`,
                                      height: '100%',
                                      backgroundColor: 'primary.main',
                                    }}
                                  />
                                </Box>
                                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                  {project.percentComplete || 0}%
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              {/* No view button on second row */}
                            </TableCell>
                          </TableRow>
                        </React.Fragment>
                        );
                      })}
                    </React.Fragment>
                  );
                })}
                {/* Unassigned projects (projects without airportId) */}
                {expandedSections.all && projectsByAirport.unassigned.length > 0 && (
                  <React.Fragment>
                    <TableRow sx={{ backgroundColor: 'grey.100' }}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', pl: 4 }}>
                          <IconButton
                            size="small"
                            onClick={() => toggleSection('unassigned')}
                            sx={{ mr: 1, p: 0.5 }}
                            aria-label={expandedSections.unassigned ? 'collapse unassigned projects' : 'expand unassigned projects'}
                          >
                            {expandedSections.unassigned ? (
                              <ExpandMore fontSize="small" />
                            ) : (
                              <ChevronRight fontSize="small" />
                            )}
                          </IconButton>
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                            Unassigned
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          {projectsByAirport.unassigned.length}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 'bold',
                            // Positive = overages/late (red), Negative = saved time/ahead (green), Zero = on time (default)
                            color: (() => {
                              const totals = getAirportTotals(projectsByAirport.unassigned);
                              return totals.projectKpi > 0 ? 'error.main' : totals.projectKpi < 0 ? 'success.main' : 'text.primary';
                            })(),
                          }}
                        >
                          {formatValue(getAirportTotals(projectsByAirport.unassigned).projectKpi)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 'bold',
                            // Positive = overages/late (red), Negative = saved time/ahead (green), Zero = on time (default)
                            color: getAirportTotals(projectsByAirport.unassigned).tenant > 0 ? 'error.main' : getAirportTotals(projectsByAirport.unassigned).tenant < 0 ? 'success.main' : 'text.primary',
                          }}
                        >
                          {formatValue(getAirportTotals(projectsByAirport.unassigned).tenant)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 'bold',
                            // Positive = overages/late (red), Negative = saved time/ahead (green), Zero = on time (default)
                            color: getAirportTotals(projectsByAirport.unassigned).fraport > 0 ? 'error.main' : getAirportTotals(projectsByAirport.unassigned).fraport < 0 ? 'success.main' : 'text.primary',
                          }}
                        >
                          {formatValue(getAirportTotals(projectsByAirport.unassigned).fraport)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 'bold',
                            // Positive = overages/late (red), Negative = saved time/ahead (green), Zero = on time (default)
                            color: getAirportTotals(projectsByAirport.unassigned).client > 0 ? 'error.main' : getAirportTotals(projectsByAirport.unassigned).client < 0 ? 'success.main' : 'text.primary',
                          }}
                        >
                          {formatValue(getAirportTotals(projectsByAirport.unassigned).client)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {/* No view button at unassigned level */}
                      </TableCell>
                    </TableRow>
                    {expandedSections.unassigned && projectsByAirport.unassigned.map((project, projectIndex) => {
                        const isSelected = selectedProjectId === project.projectId;
                        // Alternate background every 2 rows (every project = 2 rows)
                        // Use darker gray for alternate projects to make them more visually distinct
                        const rowGroupIndex = Math.floor(projectIndex);
                        const isEvenGroup = rowGroupIndex % 2 === 0;
                        const firstRowBg = isSelected 
                          ? 'rgba(25, 118, 210, 0.1)' 
                          : isEvenGroup 
                            ? 'grey.25' 
                            : 'grey.200'; // ~15% darker gray for alternate projects
                        const secondRowBg = isSelected 
                          ? 'rgba(25, 118, 210, 0.1)' 
                          : isEvenGroup 
                            ? 'grey.25' 
                            : 'grey.200'; // ~15% darker gray for alternate projects
                        return (
                  <React.Fragment key={project.projectId}>
                    {/* First row: Project name and financials */}
                    <TableRow 
                      ref={(el) => {
                        if (el) {
                          projectRefs.current[project.projectId] = el;
                        }
                      }}
                      sx={{ 
                        backgroundColor: firstRowBg,
                        borderLeft: isSelected ? '3px solid #1976d2' : 'none',
                      }}
                    >
                      <TableCell>
                        <Box sx={{ pl: 8, display: 'flex', alignItems: 'center' }}>
                          <Typography variant="body2" sx={{ mr: 1, color: 'text.secondary' }}>
                            ••
                          </Typography>
                          <Typography 
                            variant="body1"
                            component="span"
                            role="button"
                            tabIndex={0}
                            sx={{ 
                              fontWeight: 'bold',
                              color: 'primary.main',
                              fontSize: '1.1rem',
                              cursor: 'pointer',
                              '&:hover': { textDecoration: 'underline' }
                            }}
                            onClick={() => handleViewProject(project)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                handleViewProject(project);
                              }
                            }}
                          >
                            {project.name}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        {/* Blank for project level */}
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{
                            color: (() => {
                              if (project.organizationStats && project.organizationStats.length > 0) {
                                const tenantStat = project.organizationStats.find(s => s.type === 'Tenant');
                                const value = tenantStat?.kpiScore ?? project.tenant ?? 0;
                                // Positive = overages/late (red), Negative = saved time/ahead (green), Zero = on time (default)
                                return value > 0 ? 'error.main' : value < 0 ? 'success.main' : 'text.primary';
                              }
                              const value = project.tenant || 0;
                              return value > 0 ? 'error.main' : value < 0 ? 'success.main' : 'text.primary';
                            })(),
                          }}
                        >
                          {(() => {
                            if (project.organizationStats && project.organizationStats.length > 0) {
                              const tenantStat = project.organizationStats.find(s => s.type === 'Tenant');
                              return formatValue(tenantStat?.kpiScore ?? project.tenant ?? 0);
                            }
                            return formatValue(project.tenant ?? 0);
                          })()}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{
                            color: (() => {
                              if (project.organizationStats && project.organizationStats.length > 0) {
                                const fraportStat = project.organizationStats.find(s => s.type === 'Fraport');
                                const value = fraportStat?.kpiScore ?? project.fraport ?? 0;
                                // Positive = overages/late (red), Negative = saved time/ahead (green), Zero = on time (default)
                                return value > 0 ? 'error.main' : value < 0 ? 'success.main' : 'text.primary';
                              }
                              const value = project.fraport || 0;
                              return value > 0 ? 'error.main' : value < 0 ? 'success.main' : 'text.primary';
                            })(),
                          }}
                        >
                          {(() => {
                            if (project.organizationStats && project.organizationStats.length > 0) {
                              const fraportStat = project.organizationStats.find(s => s.type === 'Fraport');
                              return formatValue(fraportStat?.kpiScore ?? project.fraport ?? 0);
                            }
                            return formatValue(project.fraport ?? 0);
                          })()}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{
                            color: (() => {
                              if (project.organizationStats && project.organizationStats.length > 0) {
                                const clientStat = project.organizationStats.find(s => s.type === 'Client');
                                const value = clientStat?.kpiScore ?? project.client ?? 0;
                                // Positive = overages/late (red), Negative = saved time/ahead (green), Zero = on time (default)
                                return value > 0 ? 'error.main' : value < 0 ? 'success.main' : 'text.primary';
                              }
                              const value = project.client || 0;
                              return value > 0 ? 'error.main' : value < 0 ? 'success.main' : 'text.primary';
                            })(),
                          }}
                        >
                          {(() => {
                            if (project.organizationStats && project.organizationStats.length > 0) {
                              const clientStat = project.organizationStats.find(s => s.type === 'Client');
                              return formatValue(clientStat?.kpiScore ?? project.client ?? 0);
                            }
                            return formatValue(project.client ?? 0);
                          })()}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          aria-label={`view ${project.name}`}
                          onClick={() => handleViewProject(project)}
                        >
                          <Visibility fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                    {/* Second row: Status, dates, % complete (no view button) */}
                    <TableRow
                      sx={{
                        backgroundColor: secondRowBg,
                      }}
                    >
                      <TableCell>
                        <Box sx={{ pl: 8 }}>
                          <Chip
                            label={project.status || 'UNKNOWN'}
                            color={getStatusColor(project.status || '') as 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'}
                            size="small"
                          />
                        </Box>
                      </TableCell>
                      <TableCell>
                        {/* Blank for project level */}
                      </TableCell>
                      <TableCell>
                        {/* Blank for project level - PROJECT +/- column */}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          {project.projectStartDate ? `start: ${format(new Date(project.projectStartDate), 'MM/dd/yyyy')}` : ''}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {/* Blank - N/A text removed */}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box
                            sx={{
                              width: 60,
                              height: 8,
                              backgroundColor: 'grey.300',
                              borderRadius: 1,
                              overflow: 'hidden',
                            }}
                          >
                            <Box
                              sx={{
                                width: `${project.percentComplete || 0}%`,
                                height: '100%',
                                backgroundColor: 'primary.main',
                              }}
                            />
                          </Box>
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            {project.percentComplete || 0}%
                          </Typography>
                    </Box>
                  </TableCell>
                      <TableCell>
                        {/* No view button on second row */}
                      </TableCell>
                </TableRow>
                  </React.Fragment>
                        );
                      })}
                  </React.Fragment>
                )}
            </TableBody>
          </Table>
        </TableContainer>
        </Paper>
      )}

      {/* Task View Panel */}
      <TaskViewPanel
        isOpen={taskViewPanelOpen}
        onClose={handleTaskViewPanelClose}
        onToggle={handleTaskViewPanelToggle}
      />
    </Box>
  );
};

export default ProjectBuilderDashboard;

